import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "61";
const height = "40";
const viewBox = "0 0 61 40";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    small: { width: 17, height: 17 },
    medium: { width: 24, height: 24 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.secondary.oolong}
            fillRule="evenodd"
            d="M20.4 34.376l-2.47 5.461h.627l.642-1.433h2.952l.635 1.433h.66l-2.472-5.461H20.4zm-.96 3.479l1.24-2.766 1.231 2.766H19.44zm-8.025-.729v-.016c0-1.495-1.108-2.788-2.758-2.788s-2.774 1.308-2.774 2.804v.016c0 1.495 1.108 2.788 2.758 2.788.674 0 1.256-.216 1.713-.58l.712.65.418-.457-.72-.596a2.813 2.813 0 00.65-1.82zm-1.108 1.418l-1-.906-.41.456 1.008.86c-.334.256-.768.41-1.248.41-1.24 0-2.138-1.006-2.138-2.238v-.016c0-1.231.882-2.223 2.122-2.223 1.24 0 2.139 1.008 2.139 2.24v.014c0 .535-.179 1.023-.473 1.403zm-7.196-1.712c-1.193-.256-1.473-.543-1.473-1.053v-.017c0-.488.45-.875 1.155-.875.558 0 1.061.178 1.565.588l.356-.472c-.55-.442-1.115-.666-1.906-.666-1.03 0-1.781.627-1.781 1.479v.015c0 .892.573 1.294 1.82 1.565 1.14.24 1.41.527 1.41 1.03v.016c0 .534-.48.922-1.208.922-.752 0-1.294-.256-1.86-.767l-.38.45c.652.58 1.357.867 2.217.867 1.076 0 1.851-.604 1.851-1.534v-.016c0-.827-.558-1.268-1.766-1.532zm13.416.735c0 1.186-.635 1.79-1.635 1.79-1.038 0-1.657-.659-1.657-1.829v-3.114h-.612v3.153c0 1.534.914 2.355 2.254 2.355 1.356 0 2.263-.82 2.263-2.401v-3.107h-.613v3.153zm40.5 1.713v-1.898h2.96v-.558h-2.96v-1.851h3.307v-.559h-3.92v5.423h3.96v-.557h-3.347zM46.9 34.376l-2.47 5.461h.627l.642-1.433h2.953l.635 1.433h.658l-2.471-5.461h-.574zm-.96 3.479l1.24-2.766 1.232 2.766H45.94zM28.806 36.04v-.015c0-.426-.156-.805-.418-1.07-.342-.34-.876-.542-1.542-.542h-2.332v5.423h.613V37.73h1.55l1.571 2.107H29l-1.665-2.215c.852-.155 1.472-.682 1.472-1.58zm-3.68 1.14v-2.2H26.8c.875 0 1.387.401 1.387 1.069v.015c0 .697-.58 1.116-1.395 1.116h-1.665zm28.066 2.184c-1.193 0-2.1-.984-2.1-2.239v-.016c0-1.246.899-2.223 2.1-2.223.735 0 1.247.31 1.704.743l.418-.448c-.542-.512-1.13-.86-2.114-.86-1.597 0-2.743 1.27-2.743 2.804v.016c0 1.557 1.155 2.788 2.72 2.788.984 0 1.604-.38 2.168-.953l-.402-.395c-.519.496-1.007.783-1.75.783zm-10.9-4.95h-2.029v5.422h.611v-1.913h1.317c1.154 0 2.13-.604 2.13-1.774v-.016c0-1.068-.805-1.72-2.028-1.72zm1.41 1.758c0 .713-.589 1.193-1.487 1.193h-1.341V34.98h1.372c.875 0 1.456.402 1.456 1.178v.015zm-12.976 1.208h2.96v-.558h-2.96v-1.85h3.309v-.559h-3.92v5.423h3.959v-.557h-3.348V37.38zm6.524-.55c-1.194-.255-1.473-.542-1.473-1.053v-.016c0-.488.45-.875 1.155-.875.557 0 1.061.178 1.564.588l.357-.472c-.55-.442-1.116-.666-1.906-.666-1.03 0-1.782.627-1.782 1.479v.015c0 .892.573 1.294 1.82 1.565 1.14.24 1.41.527 1.41 1.03v.016c0 .534-.48.922-1.207.922-.753 0-1.295-.256-1.86-.767l-.38.45c.651.58 1.356.867 2.216.867 1.077 0 1.852-.604 1.852-1.534v-.016c0-.827-.558-1.268-1.766-1.532zM26.096 19a1.613 1.613 0 102.282 2.282l10.265-10.266a3.226 3.226 0 014.563 4.563L33.138 25.645a3.224 3.224 0 004.562 0l7.788-7.787a6.453 6.453 0 00-9.126-9.125L26.096 19zm15.97-6.844a1.614 1.614 0 00-2.282 0L29.518 22.421a3.225 3.225 0 01-4.562 0 1.613 1.613 0 10-2.28 2.281 6.453 6.453 0 009.125 0l10.265-10.266c.63-.63.63-1.651 0-2.28zm-17.11 5.703L35.22 7.593a1.613 1.613 0 00-2.28-2.281L22.674 15.577a3.225 3.225 0 01-4.562 0 3.224 3.224 0 010-4.562L28.18.947a3.225 3.225 0 00-4.563 0l-7.787 7.786a6.453 6.453 0 009.125 9.125zm-5.703-3.421c.63.63 1.651.63 2.28 0L31.8 4.17a3.225 3.225 0 014.562 0 1.613 1.613 0 002.281-2.281 6.452 6.452 0 00-9.125 0L19.253 12.156c-.63.629-.63 1.65 0 2.28z"
            clipRule="evenodd"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "ApiCustomerSquarespaceLogo"
});
